import React from 'react';
import { SLIM_LOGO_PROD } from '../../../utils/slimLogo';
import { resolveMediaUrl } from '../Common/DetailParts';

export const DEFAULT_TEMPLATE = {
  name: '',
  heading: 'Admit One',
  layout: 'classic',
  colors: { primary: '#6d28d9', accent: '#f59e0b', background: '#ffffff', text: '#111827' },
  bannerImage: '',
  customInfo: false,
  eventName: '',
  venue: '',
  timeText: '',
  showLogo: true,
  showPrice: true,
  showVenue: true,
  showHolder: true,
  showAllocation: true,
  terms: 'This ticket admits one person. Present the QR code at the entrance. Non-transferable.',
  isActive: true,
};

export const SAMPLE_TICKET = {
  eventTitle: 'National Marketing Summit 2026',
  date: new Date(new Date().getFullYear(), 10, 14).toISOString(),
  timeText: '6.00 pm onwards',
  venue: 'BMICH, Colombo',
  holderName: 'Nimali Perera',
  membershipId: 'AM/M/00123',
  allocationName: 'SLIM',
  price: 2500,
  currency: 'LKR',
  ticketNumber: 'TKT000123',
};

/**
 * Event details as a ticket shows them (same rule as the server's utils/ticketDetails.js):
 * the event's own "Change details" (`details`, when custom), else the template's "Custom ticket
 * info" (when on), else the event's own name and venue with no time. Empty fields fall through.
 */
export const withTicketInfo = (template, data, details) => {
  const own = details?.custom ? details : null;
  const custom = template?.customInfo ? template : null;
  const pick = (...values) => values.map((v) => String(v || '').trim()).find(Boolean) || '';
  return {
    ...data,
    eventTitle: pick(own?.eventName, custom?.eventName, data.eventTitle),
    venue: pick(own?.venue, custom?.venue, data.venue),
    timeText: pick(own?.timeText, custom?.timeText),
  };
};

const fmtDate = (v) => (v
  ? new Date(v).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })
  : '');
const fmtPrice = (n, currency = 'LKR') => (Number(n) > 0 ? `${currency} ${Number(n).toLocaleString('en-LK', { minimumFractionDigits: 2 })}` : 'Complimentary');

/** Placeholder when there is no QR (unpaid / cancelled ticket). */
const QrPlaceholder = ({ label }) => (
  <div className="flex h-full w-full items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-2 text-center text-[0.625rem] font-medium leading-tight text-gray-500">
    {label}
  </div>
);

const QrBox = ({ qrDataUrl, qrLabel, size }) => (
  <div className={`${size} shrink-0 rounded-xl bg-white p-1.5 shadow-sm ring-1 ring-black/5`}>
    {qrDataUrl
      ? <img src={qrDataUrl} alt="Ticket QR code" className="h-full w-full" style={{ imageRendering: 'pixelated' }} />
      : <QrPlaceholder label={qrLabel} />}
  </div>
);

/** One label/value pair in the ticket's fact grid. */
const SPAN = { 1: '', 2: 'col-span-2', 3: 'col-span-2 md:col-span-3' };
const Fact = ({ label, value, muted, span = 1, mono }) => (
  <div className={`min-w-0 ${SPAN[span] || ''}`}>
    <p className="text-[0.5625rem] font-semibold uppercase tracking-[0.16em]" style={{ color: muted }}>{label}</p>
    <p className={`mt-0.5 break-words text-[0.8125rem] font-semibold leading-snug ${mono ? 'font-mono tracking-wide' : ''}`}>{value}</p>
  </div>
);

/** Round cut-outs where the stub tears off (drawn in the page colour). */
const Notch = ({ className }) => <span className={`absolute h-5 w-5 rounded-full bg-gray-100 ring-1 ring-inset ring-gray-200 ${className}`} aria-hidden="true" />;

/**
 * A ticket drawn from a ticket template: main section (event, date, venue, holder, category,
 * price) and a perforated stub with the QR code. Used for template previews, viewing and printing.
 * `data`: { eventTitle, date, timeText, venue, holderName, membershipId, allocationName, price, currency, ticketNumber }
 * `vertical`: an upright ticket (header, the QR in the middle, details below), for phones and the
 * buyer's pages.
 */
const TicketCard = ({ template, data = SAMPLE_TICKET, qrDataUrl, qrLabel = 'QR appears once the ticket is paid', className = '', vertical = false }) => {
  const t = { ...DEFAULT_TEMPLATE, ...(template || {}), colors: { ...DEFAULT_TEMPLATE.colors, ...(template?.colors || {}) } };
  const c = t.colors;
  const modern = t.layout === 'modern';
  const minimal = t.layout === 'minimal';
  const bannerUrl = t.bannerImage ? resolveMediaUrl(t.bannerImage) : null;

  // Classic: brand-colour header band over a light body. Modern: the whole main section in the brand
  // colour. Minimal: light throughout with a brand-colour edge.
  const headBg = minimal ? c.background : c.primary;
  const headText = minimal ? c.text : '#ffffff';
  const bodyBg = modern ? c.primary : c.background;
  const bodyText = modern ? '#ffffff' : c.text;
  const muted = modern ? 'rgba(255,255,255,0.7)' : '#6b7280';
  const stubTint = `${c.primary}0f`; // brand colour at ~6% for the stub

  // Each fact: [label, value, { span }] where span is how many grid columns it fills.
  const fact = (label, value, span = 1, mono = false) => (value ? { label, value, span, mono } : null);
  const holderSpan = (cols) => (data.membershipId ? cols - 1 : cols);
  // Wide ticket, 3 columns: Date · Time · Price / Venue / Ticket holder · Member No
  const wideFacts = [
    fact('Date', fmtDate(data.date)),
    fact('Time', data.timeText),
    t.showPrice && fact('Price', fmtPrice(data.price, data.currency)),
    t.showVenue && fact('Venue', data.venue, 3),
    t.showHolder && fact('Ticket holder', data.holderName, holderSpan(3)),
    t.showHolder && fact('Member No', data.membershipId, 1, true),
  ].filter(Boolean);
  // Upright ticket, 2 columns: Date · Time / Venue / Holder · Member No / Price
  const tallFacts = [
    fact('Date', fmtDate(data.date)),
    fact('Time', data.timeText),
    t.showVenue && fact('Venue', data.venue, 2),
    t.showHolder && fact('Ticket holder', data.holderName, holderSpan(2)),
    t.showHolder && fact('Member No', data.membershipId, 1, true),
    t.showPrice && fact('Price', fmtPrice(data.price, data.currency), 2),
  ].filter(Boolean);

  const AllocationChip = () => (t.showAllocation && data.allocationName ? (
    <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[0.625rem] font-bold uppercase tracking-[0.12em] shadow-sm" style={{ background: c.accent, color: '#111827' }}>
      {data.allocationName}
    </span>
  ) : null);

  const Header = ({ big }) => (
    <div className="flex items-start justify-between gap-3 px-5 py-4" style={{ background: headBg, color: headText, borderTop: minimal ? `5px solid ${c.primary}` : undefined }}>
      <div className="min-w-0">
        <p className="text-[0.625rem] font-semibold uppercase tracking-[0.24em]" style={{ color: minimal ? c.primary : 'rgba(255,255,255,0.8)' }}>{t.heading}</p>
        <p className={`mt-0.5 line-clamp-2 break-words font-bold leading-tight ${big ? 'text-xl' : 'text-lg'}`}>{data.eventTitle}</p>
      </div>
      {t.showLogo && <img src={SLIM_LOGO_PROD} alt="SLIM" className="h-9 w-auto shrink-0 rounded-md bg-white p-1 shadow-sm" />}
    </div>
  );

  if (vertical) {
    return (
      <div
        className={`relative mx-auto w-full max-w-sm overflow-hidden rounded-3xl border border-gray-200 shadow-xl ${className}`}
        style={{ background: c.background, color: c.text }}
        role="img"
        aria-label={`Ticket ${data.ticketNumber} for ${data.eventTitle}`}
      >
        {bannerUrl && <img src={bannerUrl} alt="" className="block aspect-[3/1] w-full object-cover" />}
        <Header big />
        <div className="h-1" style={{ background: c.accent }} aria-hidden="true" />

        {/* QR in the middle */}
        <div className="flex flex-col items-center gap-2.5 px-5 pb-5 pt-6" style={{ background: stubTint }}>
          <QrBox qrDataUrl={qrDataUrl} qrLabel={qrLabel} size="h-52 w-52" />
          <p className="text-[0.625rem] font-semibold uppercase tracking-[0.2em] text-gray-500">Scan at entrance</p>
          <p className="font-mono text-base font-bold tracking-[0.18em]">{data.ticketNumber}</p>
          <AllocationChip />
        </div>

        {/* Tear line */}
        <div className="relative border-t-2 border-dashed border-gray-300" aria-hidden="true">
          <Notch className="-left-2.5 -top-2.5" />
          <Notch className="-right-2.5 -top-2.5" />
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-3 px-5 pb-4 pt-5">
          {tallFacts.map((f) => <Fact key={f.label} {...f} muted="#6b7280" />)}
        </div>
        {t.terms && <p className="border-t border-gray-100 px-5 py-3 text-[0.625rem] leading-snug text-gray-500">{t.terms}</p>}
      </div>
    );
  }

  return (
    <div
      className={`relative flex w-full flex-col overflow-hidden rounded-2xl border border-gray-200 shadow-lg ${className}`}
      style={{ background: c.background, color: c.text }}
      role="img"
      aria-label={`Ticket ${data.ticketNumber} for ${data.eventTitle}`}
    >
      {bannerUrl && <img src={bannerUrl} alt="" className="block h-auto max-h-48 w-full object-cover" />}

      <div className="relative flex w-full flex-col sm:flex-row" style={{ minHeight: 236 }}>
        {/* Main section */}
        <div className="flex min-w-0 flex-1 flex-col" style={{ background: bodyBg, color: bodyText }}>
          {modern ? (
            <div className="flex items-start justify-between gap-3 px-5 pt-5">
              <div className="min-w-0">
                <p className="text-[0.625rem] font-semibold uppercase tracking-[0.24em]" style={{ color: 'rgba(255,255,255,0.8)' }}>{t.heading}</p>
                <p className="mt-0.5 line-clamp-2 break-words text-xl font-bold leading-tight">{data.eventTitle}</p>
              </div>
              {t.showLogo && <img src={SLIM_LOGO_PROD} alt="SLIM" className="h-9 w-auto shrink-0 rounded-md bg-white p-1 shadow-sm" />}
            </div>
          ) : (
            <>
              <Header />
              <div className="h-1" style={{ background: c.accent }} aria-hidden="true" />
            </>
          )}

          <div className="grid flex-1 grid-cols-2 content-start gap-x-6 gap-y-3 px-5 py-4 md:grid-cols-3">
            {wideFacts.map((f) => <Fact key={f.label} {...f} muted={muted} />)}
          </div>

          {t.terms && (
            <p className="px-5 pb-4 text-[0.625rem] leading-snug" style={{ color: muted }}>{t.terms}</p>
          )}
        </div>

        {/* Tear line: vertical on wide screens, horizontal when the stub drops below */}
        <div className="relative border-t-2 border-dashed border-gray-300 sm:border-l-2 sm:border-t-0" aria-hidden="true">
          <Notch className="-left-2.5 -top-2.5 sm:-left-2.5 sm:-top-2.5" />
          <Notch className="-right-2.5 -top-2.5 sm:-bottom-2.5 sm:-left-2.5 sm:right-auto sm:top-auto" />
        </div>

        {/* Stub */}
        <div className="flex shrink-0 flex-col items-center justify-center gap-2 px-4 py-5 sm:w-48" style={{ background: stubTint, color: c.text }}>
          <QrBox qrDataUrl={qrDataUrl} qrLabel={qrLabel} size="h-36 w-36" />
          <p className="text-[0.5625rem] font-semibold uppercase tracking-[0.2em] text-gray-500">Scan at entrance</p>
          <p className="font-mono text-sm font-bold tracking-[0.16em]">{data.ticketNumber}</p>
          <AllocationChip />
        </div>
      </div>
    </div>
  );
};

export default TicketCard;
